/**
 * Modal para ver la cartera pendiente del club
 * Muestra todos los cobros pendientes agrupados por miembro
 */

import { useMemo, useState } from "react";
import { Button, Badge, Input } from "@/components/ui";
import { Card, CardContent } from "@/components/ui";
import {
  X,
  DollarSign,
  User,
  Search,
  AlertTriangle,
  Loader2,
  Download,
  ChevronDown,
  ChevronUp,
  Calendar,
} from "lucide-react";
import { usePendingCharges } from "@/hooks/useFinance";
import { format, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import type { Charge } from "@/types";
import { Timestamp } from "firebase/firestore";
import { exportToXlsx } from "@/lib/utils/exportXlsx";

interface PendingReceivablesModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string | undefined;
  clubName: string;
}

interface MemberDebt {
  memberId: string;
  personId: string;
  personName: string;
  charges: Charge[];
  totalAmount: number;
  totalPending: number;
  oldestDueDate: Date | null;
  overdueCount: number;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(timestamp: Timestamp | Date | undefined): string {
  if (!timestamp) return "N/A";
  const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;
  return format(date, "d MMM yyyy", { locale: es });
}

function isOverdue(dueDate: Timestamp | Date | undefined): boolean {
  if (!dueDate) return false;
  const date = dueDate instanceof Timestamp ? dueDate.toDate() : dueDate;
  return date < new Date();
}

function getDaysOverdue(dueDate: Timestamp | Date | undefined): number {
  if (!dueDate) return 0;
  const date = dueDate instanceof Timestamp ? dueDate.toDate() : dueDate;
  return differenceInDays(new Date(), date);
}

export function PendingReceivablesModal({
  isOpen,
  onClose,
  clubId,
  clubName,
}: PendingReceivablesModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(
    new Set(),
  );
  const [sortBy, setSortBy] = useState<"name" | "amount" | "overdue">("amount");

  const { data: pendingCharges = [], isLoading } = usePendingCharges(clubId);

  // Agrupar por miembro
  const memberDebts = useMemo(() => {
    const grouped: Record<string, MemberDebt> = {};

    pendingCharges.forEach((charge) => {
      if (!grouped[charge.memberId]) {
        grouped[charge.memberId] = {
          memberId: charge.memberId,
          personId: charge.personId,
          personName: charge.personName,
          charges: [],
          totalAmount: 0,
          totalPending: 0,
          oldestDueDate: null,
          overdueCount: 0,
        };
      }

      const debt = grouped[charge.memberId];
      debt.charges.push(charge);
      debt.totalAmount += charge.amount;
      debt.totalPending += charge.amount - (charge.amountPaid || 0);

      if (isOverdue(charge.dueDate)) {
        debt.overdueCount++;
      }

      const dueDate =
        charge.dueDate instanceof Timestamp
          ? charge.dueDate.toDate()
          : charge.dueDate;
      if (dueDate && (!debt.oldestDueDate || dueDate < debt.oldestDueDate)) {
        debt.oldestDueDate = dueDate;
      }
    });

    let result = Object.values(grouped);

    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((d) => d.personName.toLowerCase().includes(term));
    }

    // Ordenar
    result.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.personName.localeCompare(b.personName);
        case "amount":
          return b.totalPending - a.totalPending;
        case "overdue":
          return b.overdueCount - a.overdueCount;
        default:
          return 0;
      }
    });

    return result;
  }, [pendingCharges, searchTerm, sortBy]);

  // Totales
  const totals = useMemo(() => {
    const total = memberDebts.reduce((sum, d) => sum + d.totalPending, 0);
    const overdue = memberDebts.reduce((sum, d) => {
      return (
        sum +
        d.charges
          .filter((c) => isOverdue(c.dueDate))
          .reduce((s, c) => s + c.amount - (c.amountPaid || 0), 0)
      );
    }, 0);
    const membersWithDebt = memberDebts.length;
    const overdueCharges = memberDebts.reduce(
      (sum, d) => sum + d.overdueCount,
      0,
    );

    return { total, overdue, membersWithDebt, overdueCharges };
  }, [memberDebts]);

  const toggleMemberExpanded = (memberId: string) => {
    const newExpanded = new Set(expandedMembers);
    if (newExpanded.has(memberId)) {
      newExpanded.delete(memberId);
    } else {
      newExpanded.add(memberId);
    }
    setExpandedMembers(newExpanded);
  };

  // Exportar a Excel
  const exportToCSV = () => {
    type Row = {
      personName: string;
      conceptName: string;
      amount: number;
      amountPaid: number;
      pending: number;
      dueDate: string;
      status: string;
    };
    const rows: Row[] = [];
    memberDebts.forEach((debt) => {
      debt.charges.forEach((charge) => {
        rows.push({
          personName: debt.personName,
          conceptName: charge.conceptName,
          amount: charge.amount,
          amountPaid: charge.amountPaid || 0,
          pending: charge.amount - (charge.amountPaid || 0),
          dueDate: formatDate(charge.dueDate),
          status: isOverdue(charge.dueDate) ? "Vencido" : "Pendiente",
        });
      });
    });
    exportToXlsx<Row>({
      filename: `cartera_${clubName.replace(/\s/g, "_")}_${format(new Date(), "yyyy-MM-dd")}`,
      sheetName: "Cartera",
      rows,
      columns: [
        { header: "Miembro", key: "personName", width: 28 },
        { header: "Concepto", key: "conceptName", width: 28 },
        { header: "Monto", key: "amount", width: 14 },
        { header: "Pagado", key: "amountPaid", width: 14 },
        { header: "Pendiente", key: "pending", width: 14 },
        { header: "Vencimiento", key: "dueDate", width: 14 },
        { header: "Estado", key: "status", width: 12 },
      ],
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-md shadow-amber-500/25">
              <DollarSign className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Cartera Pendiente</h2>
              <p className="text-sm text-gray-500">{clubName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* Resumen */}
            <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-0 shadow-sm">
                <CardContent className="p-3 text-center">
                  <p className="text-xl font-bold text-blue-600">
                    {formatCurrency(totals.total)}
                  </p>
                  <p className="text-xs font-medium text-blue-700">
                    Total Pendiente
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-red-50 to-red-100/50 border-0 shadow-sm">
                <CardContent className="p-3 text-center">
                  <p className="text-xl font-bold text-red-600">
                    {formatCurrency(totals.overdue)}
                  </p>
                  <p className="text-xs font-medium text-red-700">Vencido</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-0 shadow-sm">
                <CardContent className="p-3 text-center">
                  <p className="text-xl font-bold text-amber-600">
                    {totals.membersWithDebt}
                  </p>
                  <p className="text-xs font-medium text-amber-700">
                    Miembros con Deuda
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-50 to-orange-100/50 border-0 shadow-sm">
                <CardContent className="p-3 text-center">
                  <p className="text-xl font-bold text-orange-600">
                    {totals.overdueCharges}
                  </p>
                  <p className="text-xs font-medium text-orange-700">
                    Cobros Vencidos
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Filtros */}
            <div className="p-4 border-b flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="relative flex-1 w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar miembro..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant={sortBy === "amount" ? "primary" : "secondary"}
                    onClick={() => setSortBy("amount")}
                  >
                    Monto
                  </Button>
                  <Button
                    size="sm"
                    variant={sortBy === "overdue" ? "primary" : "secondary"}
                    onClick={() => setSortBy("overdue")}
                  >
                    Vencidos
                  </Button>
                  <Button
                    size="sm"
                    variant={sortBy === "name" ? "primary" : "secondary"}
                    onClick={() => setSortBy("name")}
                  >
                    Nombre
                  </Button>
                </div>
              </div>
            </div>

            {/* Lista de deudas por miembro */}
            <div className="flex-1 overflow-y-auto p-4">
              {memberDebts.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <DollarSign className="w-8 h-8 text-green-500" />
                  </div>
                  <p className="font-medium">No hay cobros pendientes</p>
                  <p className="text-sm">Todos los cobros han sido pagados</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {memberDebts.map((debt) => (
                    <div
                      key={debt.memberId}
                      className="border-2 border-gray-100 rounded-xl overflow-hidden"
                    >
                      {/* Header del miembro */}
                      <button
                        onClick={() => toggleMemberExpanded(debt.memberId)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-sm shadow-primary-500/25">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-gray-900">
                              {debt.personName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {debt.charges.length} cobro
                              {debt.charges.length !== 1 ? "s" : ""} pendiente
                              {debt.charges.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {debt.overdueCount > 0 && (
                            <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {debt.overdueCount} vencido
                              {debt.overdueCount !== 1 ? "s" : ""}
                            </Badge>
                          )}
                          <span className="font-bold text-lg text-gray-900">
                            {formatCurrency(debt.totalPending)}
                          </span>
                          {expandedMembers.has(debt.memberId) ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {/* Detalle de cobros */}
                      {expandedMembers.has(debt.memberId) && (
                        <div className="divide-y">
                          {debt.charges.map((charge) => {
                            const overdue = isOverdue(charge.dueDate);
                            const daysOverdue = getDaysOverdue(charge.dueDate);
                            const pending =
                              charge.amount - (charge.amountPaid || 0);

                            return (
                              <div
                                key={charge.id}
                                className={`p-4 ${overdue ? "bg-red-50" : "bg-white"}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {charge.conceptName}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        Vence: {formatDate(charge.dueDate)}
                                      </span>
                                      {overdue && (
                                        <span className="text-red-600 font-medium">
                                          ({daysOverdue} días vencido)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-gray-900">
                                      {formatCurrency(pending)}
                                    </p>
                                    {(charge.amountPaid || 0) > 0 && (
                                      <p className="text-xs text-gray-500">
                                        de {formatCurrency(charge.amount)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-4 border-t">
              <Button
                variant="secondary"
                onClick={exportToCSV}
                disabled={memberDebts.length === 0}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Exportar Excel
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
